import { useMemo, useState, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { LoadingOverlay } from "@/components/ui/LoadingOverlay";
import { useModules } from "@/context/ModulesContext";
import {
  BarChart2,
  BriefcaseBusiness,
  Banknote,
  CalendarDays,
  Calculator,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Gamepad2,
  HardHat,
  Headset,
  LayoutGrid,
  LogOut,
  Megaphone,
  Menu,
  Settings,
  ShoppingCart,
  TrendingUp,
  MessageCircle,
  Truck,
  Wallet,
  Users,
  Wrench,
} from "lucide-react";
import { GamePasswordModal, isGameUnlocked, GAME_SESSION_KEY } from "@/pages/Game/GamePasswordModal";
import { GameScreen } from "@/pages/Game";
import { cn } from "@/utils/cn";
import { APP_SHORT_NAME, ROUTES } from "@/config/constants";
import { NavLink, useNavigate } from "react-router-dom";
import { useAppAccess } from "@/hooks/useAppAccess";
import {
  canAccessLocacoesModule,
  canAccessSolicitacaoPagamentoModule,
  isMarketingOnlyUser,
  type AppAccessKey,
} from "@/lib/appAccess";
import { ChangePasswordControl } from "@/components/auth/ChangePasswordControl";

/* ─── Itens de navegação (expandir conforme novos módulos forem criados) ── */
const navItemsBase: Array<{
  label: string;
  to: string;
  icon: typeof ShoppingCart;
  access: AppAccessKey;
}> = [
  {
    label: "Suprimentos",
    to: ROUTES.SUPRIMENTOS,
    icon: ShoppingCart,
    access: "suprimentos",
  },
  {
    label: "Financeiro",
    to: ROUTES.FINANCEIRO,
    icon: DollarSign,
    access: "financeiro",
  },
  {
    label: "Comercial",
    to: ROUTES.COMERCIAL,
    icon: TrendingUp,
    access: "comercial",
  },
  {
    label: "Orçamento",
    to: ROUTES.ORCAMENTO,
    icon: Calculator,
    access: "orcamentos",
  },
  {
    label: "Gerencial",
    to: ROUTES.GERENCIAL,
    icon: BriefcaseBusiness,
    access: "gerencial",
  },
  {
    label: "Chão de Obra",
    to: ROUTES.CHAO_DE_OBRA,
    icon: HardHat,
    access: "chaoDeObra",
  },
  {
    label: "Solicitação de Pagamento",
    to: ROUTES.SOLICITACAO_PAGAMENTO,
    icon: Banknote,
    access: "solicitacaoPagamento",
  },
  {
    label: "Tarefas",
    to: ROUTES.TAREFAS,
    icon: CalendarDays,
    access: "tarefas",
  },
  {
    label: "Semog Locações",
    to: ROUTES.LOCACOES,
    icon: Truck,
    access: "locacoes",
  },
  {
    label: "Manutenção",
    to: ROUTES.MANUTENCAO,
    icon: Wrench,
    access: "manutencao",
  },
  {
    label: "Folha de Pagamentos",
    to: ROUTES.FOLHA_PAGAMENTOS,
    icon: Wallet,
    access: "folhaPagamentos",
  },
  {
    label: "Gente & Gestão",
    to: ROUTES.GENTE_GESTAO,
    icon: Users,
    access: "genteGestao",
  },
  {
    label: "Marketing",
    to: ROUTES.MARKETING,
    icon: Megaphone,
    access: "marketing",
  },
];

/* Mensagens de carregamento por módulo */
const loadingMessages: Record<string, string> = {
  [ROUTES.FINANCEIRO]:  "Carregando Financeiro…",
  [ROUTES.SUPRIMENTOS]: "Carregando Suprimentos…",
  [ROUTES.COMERCIAL]:   "Carregando Comercial…",
  [ROUTES.ORCAMENTO]:   "Carregando Orçamento…",
  [ROUTES.GERENCIAL]:   "Carregando Gerencial…",
  [ROUTES.CHAO_DE_OBRA]: "Carregando Chão de Obra…",
  [ROUTES.SOLICITACAO_PAGAMENTO]: "Carregando Solicitação de Pagamento…",
  [ROUTES.TAREFAS]: "Carregando Tarefas…",
  [ROUTES.LOCACOES]: "Carregando Semog Locações…",
  [ROUTES.MANUTENCAO]: "Carregando Manutenção…",
  [ROUTES.FOLHA_PAGAMENTOS]: "Carregando Folha de Pagamentos…",
  [ROUTES.GENTE_GESTAO]: "Carregando Gente & Gestão…",
};

export function AppLayout() {
  const { user, logout } = useAuth();
  const { can, isAdmin } = useAppAccess();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { financeiro, suprimentos, comercial, orcamento, gerencial, chaoDeObra } = useModules();
  const marketingOnly = isMarketingOnlyUser(can, {
    locacoesOperador: user?.locacoesOperador,
    role: user?.role,
  });
  const navItems = useMemo(() => {
    const filtered = navItemsBase.filter((item) => {
      if (item.access === "solicitacaoPagamento") {
        return canAccessSolicitacaoPagamentoModule(can);
      }
      if (item.access === "locacoes") {
        return canAccessLocacoesModule(can, user?.locacoesOperador);
      }
      return can(item.access);
    });
    if (marketingOnly) {
      return filtered.filter((item) => item.access === "marketing");
    }
    return filtered;
  }, [can, marketingOnly, user?.locacoesOperador]);
  const [sidebarOpen,     setSidebarOpen]     = useState(true);
  const [mobileMenuOpen,  setMobileMenuOpen]  = useState(false);
  const [gameOpen,        setGameOpen]        = useState(false);
  const [passwordOpen,    setPasswordOpen]    = useState(false);

  const handleGameIconClick = useCallback(() => {
    if (isGameUnlocked()) {
      setGameOpen(true);
    } else {
      setPasswordOpen(true);
    }
  }, []);

  const handleGameUnlocked = useCallback(() => {
    setPasswordOpen(false);
    setGameOpen(true);
  }, []);

  const handleGameClose = useCallback(() => {
    setGameOpen(false);
    sessionStorage.removeItem(GAME_SESSION_KEY);
  }, []);

  const collapseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const navigateAndCollapse = useCallback(
    (to: string) => {
      navigate(to);
      collapseSidebar();
    },
    [navigate, collapseSidebar],
  );

  /* Determina se o módulo da rota atual está carregando (overlay igual ao Financeiro) */
  const isLoading = useMemo(() => {
    if (location.pathname.startsWith(ROUTES.CHAO_DE_OBRA)) {
      return chaoDeObra.listLoading || chaoDeObra.unitTransitionLoading;
    }
    switch (location.pathname) {
      case ROUTES.FINANCEIRO:
        return financeiro.loading;
      case ROUTES.SUPRIMENTOS:
        return suprimentos.loading;
      case ROUTES.COMERCIAL:
        return comercial.loading;
      case ROUTES.ORCAMENTO:
        return orcamento.loading;
      case ROUTES.GERENCIAL:
        return gerencial.loading;
      default:
        return false;
    }
  }, [
    location.pathname,
    financeiro.loading,
    suprimentos.loading,
    comercial.loading,
    orcamento.loading,
    gerencial.loading,
    chaoDeObra.listLoading,
    chaoDeObra.unitTransitionLoading,
  ]);

  return (
    <div className="h-dvh overflow-hidden bg-slate-50">

      {/* ── Sidebar (fixa na viewport — não acompanha scroll do conteúdo) ── */}
      <aside
        className={cn(
          "no-print fixed inset-y-0 left-0 z-30 hidden md:flex flex-col bg-slate-900 transition-[width] duration-300 ease-in-out",
          sidebarOpen ? "w-56" : "w-16",
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-slate-700/50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
              <BarChart2 className="w-4 h-4 text-white" />
            </div>
            {sidebarOpen && (
              <span className="text-sm font-bold text-white truncate">
                {APP_SHORT_NAME} BI
              </span>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-2 flex flex-col gap-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={collapseSidebar}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  "text-slate-400 hover:text-white hover:bg-slate-700/60",
                  isActive && "bg-brand-600/20 text-brand-400 hover:bg-brand-600/30 hover:text-brand-300"
                )
              }
              title={!sidebarOpen ? item.label : undefined}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {!marketingOnly && (
          <div className="px-2 border-b border-slate-700/50 pb-2 space-y-1">
            <button
              onClick={() => navigateAndCollapse(ROUTES.HOME)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                "text-slate-400 hover:text-white hover:bg-slate-700/60"
              )}
              title="Menu Principal"
            >
              <LayoutGrid className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">Menu Principal</span>}
            </button>
            {isAdmin && (
              <button
                onClick={() => navigateAndCollapse(ROUTES.ADMIN_ACESSO)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  "text-slate-400 hover:text-white hover:bg-slate-700/60"
                )}
                title="Usuários e grupos"
              >
                <Settings className="w-4 h-4 flex-shrink-0" />
                {sidebarOpen && <span className="truncate">Acessos</span>}
              </button>
            )}
            <button
              onClick={() => navigateAndCollapse(ROUTES.CHAMADOS_TI)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                "text-slate-400 hover:text-white hover:bg-slate-700/60",
                location.pathname === ROUTES.CHAMADOS_TI &&
                  "bg-brand-600/20 text-brand-400 hover:bg-brand-600/30 hover:text-brand-300",
              )}
              title="Chamados TI"
            >
              <Headset className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">Chamados TI</span>}
            </button>
            {isAdmin && (
              <button
                onClick={() => navigateAndCollapse(ROUTES.WHATSAPP)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  "text-slate-400 hover:text-white hover:bg-slate-700/60"
                )}
                title="WhatsApp"
              >
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                {sidebarOpen && <span className="truncate">WhatsApp</span>}
              </button>
            )}
          </div>
        )}

        {/* Toggle collapse */}
        <div className="px-2 py-3 border-t border-slate-700/50">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors"
            title={sidebarOpen ? "Recolher menu" : "Expandir menu"}
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>
      </aside>

      {/* ── Área principal (deslocada pela largura da sidebar) ───────── */}
      <div
        className={cn(
          "flex h-dvh min-h-0 flex-col overflow-hidden transition-[margin] duration-300 ease-in-out",
          sidebarOpen ? "md:ml-56" : "md:ml-16",
        )}
      >

        {/* Header */}
        <header className="no-print flex-shrink-0 flex items-center justify-between h-16 px-4 md:px-6 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            {/* Menu mobile */}
            <button
              className="md:hidden p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div>
              <h2 className="text-sm font-semibold text-slate-800 leading-tight">
                Painel Gerencial SEMOG
              </h2>
              <p className="text-xs text-slate-400 hidden sm:block">
                SEMOG Engenharia
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-1">
                <div className="flex flex-col items-end">
                  <span className="text-xs font-semibold text-slate-700">
                    {user.displayName}
                  </span>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                    {user.role}
                  </span>
                </div>
                <ChangePasswordControl hidden={user.id === "dev"} />
              </div>
            )}

            <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-sm select-none">
              {user?.displayName?.[0]?.toUpperCase() ?? "?"}
            </div>

            {!marketingOnly && (
              <button
                onClick={handleGameIconClick}
                className="relative flex-shrink-0 group"
                title="Gestor Master"
                aria-label="Abrir Gestor Master"
              >
                <span
                  className="absolute -inset-1 rounded-xl opacity-75 animate-pulse blur-sm"
                  style={{
                    background: "linear-gradient(135deg, #7c3aed, #2563eb, #059669, #d97706)",
                  }}
                />
                <span
                  className="relative flex items-center justify-center w-8 h-8 rounded-lg shadow-lg"
                  style={{
                    background: "linear-gradient(135deg, #6d28d9 0%, #1d4ed8 55%, #047857 100%)",
                    boxShadow: "0 0 10px rgba(109,40,217,0.6), 0 0 20px rgba(29,78,216,0.3)",
                  }}
                >
                  <Gamepad2 className="w-4 h-4 text-white drop-shadow" />
                </span>
              </button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              leftIcon={<LogOut className="w-4 h-4" />}
              className="text-slate-500"
            >
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>

        {/* Mobile nav overlay */}
        {mobileMenuOpen && (
          <div className="no-print md:hidden fixed inset-0 z-40 flex">
            <div className="w-64 bg-slate-900 flex flex-col">
              <div className="flex items-center h-16 px-4 border-b border-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
                    <BarChart2 className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-bold text-white">{APP_SHORT_NAME} BI</span>
                </div>
              </div>
              <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5 overflow-y-auto">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-slate-400 hover:text-white hover:bg-slate-700/60",
                        isActive && "bg-brand-600/20 text-brand-400"
                      )
                    }
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                ))}
                {!marketingOnly && isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(ROUTES.ADMIN_ACESSO);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-700/60 w-full text-left"
                  >
                    <Settings className="w-4 h-4" />
                    Acessos
                  </button>
                )}
                {!marketingOnly && (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(ROUTES.CHAMADOS_TI);
                    }}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-700/60 w-full text-left",
                      location.pathname === ROUTES.CHAMADOS_TI &&
                        "bg-brand-600/20 text-brand-400",
                    )}
                  >
                    <Headset className="w-4 h-4" />
                    Chamados TI
                  </button>
                )}
                {!marketingOnly && isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(ROUTES.WHATSAPP);
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-700/60 w-full text-left"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </button>
                )}
              </nav>
            </div>
            <div
              className="flex-1 bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            />
          </div>
        )}

        {/* Conteúdo da página — scroll interno; sidebar permanece fixa */}
        <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-4 md:p-6">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Jogo — overlay full-screen sobre toda a interface */}
      {gameOpen && (
        <div className="fixed inset-0 z-[150]">
          <GameScreen onClose={handleGameClose} />
        </div>
      )}

      {/* Modal de senha do jogo */}
      <GamePasswordModal
        open={passwordOpen}
        onSuccess={handleGameUnlocked}
        onClose={() => setPasswordOpen(false)}
      />

      {/* Overlay de carregamento — mesmo padrão (LoadingOverlay) em todos os módulos */}
      {isLoading && (
        <LoadingOverlay
          message={
            location.pathname.startsWith(ROUTES.CHAO_DE_OBRA) &&
            chaoDeObra.unitTransitionLoading &&
            !chaoDeObra.listLoading
              ? "Carregando unidade…"
              : (loadingMessages[location.pathname] ??
                (location.pathname.startsWith(ROUTES.GERENCIAL)
                  ? loadingMessages[ROUTES.GERENCIAL]
                  : location.pathname.startsWith(ROUTES.CHAO_DE_OBRA)
                    ? loadingMessages[ROUTES.CHAO_DE_OBRA]
                    : "Carregando…"))
          }
        />
      )}
    </div>
  );
}
